// src/cbt_lms/creator_login.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Helmet } from 'react-helmet-async';
import Cookies from 'universal-cookie';
import { LoginUrl, gmailLogin } from './serverurl';

const CreatorLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [processing, setProcessing] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const cookies = new Cookies();
  const navigate = useNavigate();

  const setSessionCookies = (responseData, userType) => {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 7);
    cookies.set('auto_login_code', responseData.auto_login_code, { expires: expiryDate });
    cookies.set('auth_key', responseData.access_token, { expires: expiryDate });
    cookies.set('access_token', responseData.access_token, { expires: expiryDate });
    cookies.set('name', responseData.name, { expires: expiryDate });
    cookies.set('email', responseData.email, { expires: expiryDate });
    cookies.set('authenticated', true, { expires: expiryDate });
    cookies.set('user_type', userType, { expires: expiryDate });
    if (responseData.user_id) cookies.set('user_id', responseData.user_id, { expires: expiryDate });
  };

  const handleCreatorLoginSuccess = (responseData) => {
    setSessionCookies(responseData, 'creator');
    toast.success('Welcome back, creator!');
    navigate('/cbt_creator/dashboard');
  };

  const validateForm = () => {
    const errors = {};
    if (!email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) errors.email = 'Invalid email address';
    if (!password) errors.password = 'Password is required';
    return errors;
  };

  const loginForm = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setProcessing(true);
    setDisabled(true);
    try {
      const response = await axios.post(LoginUrl, {
        email, password, browser: true, user_type: 'creator'
      });
      if (response.status === 200 && response.data.serverstatus !== 400) {
        handleCreatorLoginSuccess(response.data);
      } else {
        toast.error(response.data.message || 'Login failed');
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 401) toast.error('Invalid password. Reset it?');
      else if (status === 404) toast.error('Email not found');
      else toast.error('Login failed. Try again.');
    } finally {
      setProcessing(false);
      setDisabled(false);
    }
  };

  const googleSuccess = async (response) => {
    try {
      jwtDecode(response.credential);
      setProcessing(true);
      setDisabled(true);
      const res = await axios.post(gmailLogin, {
        token: response.credential, browser: true, user_type: 'creator'
      });
      if (res.status === 200) handleCreatorLoginSuccess(res.data);
      else toast.error(res.data?.message || 'Google login failed');
    } catch (err) {
      toast.error('Google authentication failed');
    } finally {
      setProcessing(false);
      setDisabled(false);
    }
  };

  const googleError = () => toast.error('Google login failed. Try another method.');

  return (
    <>
      <Helmet><title>Creator Login | CBTHost</title></Helmet>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-100 px-4 py-12">
        <div className="max-w-md w-full bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden transition-all duration-500 hover:shadow-3xl">
          <div className="p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-lg mb-4">
                <i className="fas fa-chalkboard-user text-white text-2xl"></i>
              </div>
              <h2 className="text-3xl font-extrabold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                Creator Portal
              </h2>
              <p className="text-gray-500 mt-2">Sign in to manage your tests & analytics</p>
            </div>

            <div className="flex justify-center mb-8">
              <GoogleLogin onSuccess={googleSuccess} onError={googleError} theme="filled_blue" size="large" shape="pill" width="280" />
            </div>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300" /></div>
              <div className="relative flex justify-center text-sm"><span className="px-3 bg-white text-gray-500">Or continue with email</span></div>
            </div>

            <form onSubmit={loginForm} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <div className="relative mt-1">
                  <i className="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} disabled={disabled}
                    className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 transition ${formErrors.email ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                    placeholder="you@example.com" />
                </div>
                {formErrors.email && <p className="text-red-500 text-sm mt-1"><i className="fas fa-exclamation-circle mr-1"></i>{formErrors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <div className="relative mt-1">
                  <i className="fas fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} disabled={disabled}
                    className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 transition ${formErrors.password ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                    placeholder="••••••••" />
                </div>
                {formErrors.password && <p className="text-red-500 text-sm mt-1"><i className="fas fa-exclamation-circle mr-1"></i>{formErrors.password}</p>}
              </div>

              <div className="flex justify-end">
                <a href="/password-reset" className="text-sm text-blue-600 hover:underline">Forgot password?</a>
              </div>

              <button type="submit" disabled={disabled}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 rounded-xl shadow-md transition-all transform hover:-translate-y-0.5 disabled:opacity-50">
                {processing ? <><i className="fas fa-spinner fa-spin mr-2"></i>Signing in...</> : <><i className="fas fa-sign-in-alt mr-2"></i>Sign in as Creator</>}
              </button>
            </form>

            <div className="mt-8 text-center border-t pt-6">
              <p className="text-gray-600">New to CBTHost? <a href="/sign-up" className="font-semibold text-blue-600 hover:underline">Create an account</a></p>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-center" theme="colored" />
    </>
  );
};

export default CreatorLogin;